import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { DockCommunityAvatar } from "@circle-react/components/CircleAi/Dock/DockCommunityAvatar";
import {
  getAutomaticLoginRootUrl,
  notifyCommunitySwitch,
} from "@circle-react/components/CommunitySwitcher/utils";
import { validateInviteMembersQueryParams } from "@circle-react/components/SettingsApp/ManageMembers/InviteMembers/queryParamsSchema";
import {
  useIsCommunityAdmin,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { useIsAdminOrModerator } from "@circle-react/contexts/punditUserContext";
import { isPromoCodeEnabled } from "@circle-react/helpers/communityHelpers";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { useCommunitySwitcher } from "@circle-react/hooks/community_switcher/useCommunitySwitcherApi";
import { useThemeContext } from "@circle-react/providers/Theme";
import type { IconName } from "@circleco/compass/components/Icon";
import { Icon } from "@circleco/compass/components/Icon";
import { useNavigate, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";

export type CommunityRow =
  | "divider"
  | {
      closeOnClick?: boolean;
      icon?: IconName;
      label: string;
      onClick: () => void;
      prefix?: ReactNode;
      suffix?: ReactNode;
    };

interface CommunityHeader {
  canGoBack: boolean;
  communityName: string;
  currentAvatar: ReactNode;
  goBackToCommunity: () => void;
  rows: CommunityRow[];
}

export const useCommunityHeader = (): CommunityHeader => {
  const setUserMode = useCircleAIShellStore(s => s.setUserMode);
  const setAdminDockForced = useCircleAIShellStore(s => s.setAdminDockForced);
  const setConversationPanelState = useCircleAIShellStore(
    s => s.setConversationPanelState,
  );
  const { currentCommunity } = usePunditUserContext();
  const isAdmin = useIsCommunityAdmin();
  const isAdminOrModerator = useIsAdminOrModerator();
  const { isDarkAppearance } = useThemeContext();
  const { communities, token } = useCommunitySwitcher();
  const navigate = useNavigate();
  const pathname = useLocation({ select: location => location.pathname });
  const isPromoLockedToPlansPage = isPromoCodeEnabled(currentCommunity);

  const communityName = currentCommunity?.name ?? "";

  const goToHome = () => {
    if (currentCommunity?.home_page_enabled) {
      void navigate({ to: "/feed" });
      return;
    }

    // Rails layout won't refresh when the router navigates to "/" -- when feed is disabled, we need to redirect to the root path.
    window.location.href = "/";
  };

  const goBackToCommunity = () => {
    setUserMode("community");
    setAdminDockForced(false);
    setConversationPanelState("closed");
    goToHome();
  };

  const avatar = (options: {
    darkIconUrl?: string | null;
    lightIconUrl?: string | null;
    name?: string | null;
    lightBrandColor?: string;
    darkBrandColor?: string;
  }) => (
    <DockCommunityAvatar
      iconUrl={
        (isDarkAppearance ? options.darkIconUrl : options.lightIconUrl) ?? ""
      }
      name={options.name ?? ""}
      isDarkAppearance={isDarkAppearance}
      lightBrandColor={options.lightBrandColor}
      darkBrandColor={options.darkBrandColor}
    />
  );

  const currentAvatar = avatar({
    darkIconUrl: currentCommunity?.dark_icon_url,
    lightIconUrl: currentCommunity?.icon_url,
    name: communityName,
    lightBrandColor: currentCommunity?.prefs?.brand_color?.light,
    darkBrandColor: currentCommunity?.prefs?.brand_color?.dark,
  });

  const backToCommunity: CommunityRow = {
    icon: "arrow-left",
    label: t("admin_nav_v4.back_to_community"),
    onClick: goBackToCommunity,
  };

  const inviteMembers: CommunityRow = {
    icon: "people-add",
    label: t("circle_ai_dock.community_menu.invite_members"),
    onClick: () =>
      void navigate({
        to: settingsNavPathV3.members.invite,
        search: validateInviteMembersQueryParams({
          from: pathname,
        }),
      }),
  };

  const settings: CommunityRow = {
    icon: "settings-gear",
    label: t("circle_ai_dock.community_menu.settings"),
    onClick: () => {
      setUserMode("admin");
      void navigate({ to: "/settings" });
    },
  };

  const actions: CommunityRow[] = isPromoLockedToPlansPage
    ? []
    : isAdmin
      ? [backToCommunity, inviteMembers, settings]
      : isAdminOrModerator
        ? [backToCommunity]
        : [inviteMembers];

  const otherCommunities = communities.filter(
    community => !community.is_current_community,
  );

  const switcherRows: CommunityRow[] = otherCommunities.map(community => ({
    label: community.name,
    prefix: (
      <span className="-mx-1 flex shrink-0">
        {avatar({
          darkIconUrl: community.dark_icon_url,
          lightIconUrl: community.light_icon_url,
          name: community.name,
          lightBrandColor: community.light_brand_color,
          darkBrandColor: community.dark_brand_color,
        })}
      </span>
    ),
    onClick: () => {
      notifyCommunitySwitch();
      globalThis.location.href = getAutomaticLoginRootUrl(
        community.root_url,
        token,
      );
    },
  }));

  const rows: CommunityRow[] = [
    {
      label: communityName,
      prefix: <span className="-mx-1 flex shrink-0">{currentAvatar}</span>,
      suffix: (
        <Icon
          name="checkmark"
          size="sm"
          className="shrink-0"
          color="secondary"
        />
      ),
      onClick: () => undefined,
      closeOnClick: false,
    },
    ...actions,
    ...(switcherRows.length > 0 ? (["divider"] as const) : []),
    ...switcherRows,
  ];

  return {
    canGoBack: !isPromoLockedToPlansPage && isAdminOrModerator,
    communityName,
    currentAvatar,
    goBackToCommunity,
    rows,
  };
};
